/**
 * VerifyEmailStep Component
 * Step 2: Enter the 6-digit verification code sent to the user's email.
 * Uses the same 6-digit input pattern from the existing VerificationStep.
 */

import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { Button, IconContainer } from '@/components/ui';

interface VerifyEmailStepProps {
  email: string;
  verificationCode: string;
  error: string | null;
  isLoading: boolean;
  onVerificationCodeChange: (code: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  onResendCode: () => void;
  canProceed: boolean;
}

export const VerifyEmailStep: React.FC<VerifyEmailStepProps> = ({
  email,
  verificationCode,
  error,
  isLoading,
  onVerificationCodeChange,
  onBack,
  onSubmit,
  onResendCode,
  canProceed,
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canProceed) {
      onSubmit();
    }
  };

  // Handle individual digit input
  const handleDigitChange = (index: number, value: string) => {
    const digit = value.slice(-1);
    if (!/^\d*$/.test(digit)) return;

    const newCode = verificationCode.split('');
    newCode[index] = digit;
    const updatedCode = newCode.join('').slice(0, 6);
    onVerificationCodeChange(updatedCode);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onVerificationCodeChange(pastedData);
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <IconContainer variant="success" size="lg" className="mx-auto mb-3">
          <Mail className="w-6 h-6" />
        </IconContainer>
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {t('signup.checkYourEmail')}
        </h2>
        <p className="text-slate-600">
          {t('signup.sentCodeTo')}
        </p>
        <p className="font-medium text-slate-900 mt-1">{email}</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Code Input */}
      <div className="flex justify-center gap-2" onPaste={handlePaste}>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={verificationCode[index] || ''}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-300 rounded-xl
                     focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                     transition-colors"
            autoComplete="one-time-code"
          />
        ))}
      </div>

      {/* Resend Code */}
      <div className="text-center">
        <button
          type="button"
          onClick={onResendCode}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-emerald-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('signup.resendCode')}
        </button>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={onBack}
          variant="ghost"
          size="lg"
          shape="rounded"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>

        <Button
          type="submit"
          disabled={!canProceed || isLoading}
          variant="gradient-emerald"
          size="lg"
          shape="rounded"
          className="flex-1"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('signup.verifying')}
            </span>
          ) : (
            t('signup.verifyEmail')
          )}
        </Button>
      </div>
    </form>
  );
};

export default VerifyEmailStep;
